import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { sankey, sankeyCenter, sankeyLinkHorizontal } from "d3-sankey";
import { useEffect, useMemo, useRef, useState } from "react";
import { getSankeyNodeColor } from "../../pages/Projects/ProjectDetails.helper";
import { SANKEY_NODE_WIDTH, SANKEY_SVG_HEIGHT, SANKEY_SVG_WIDTH } from "./SankeyDiagram.interface";
const MARGIN_Y = 15;
const MARGIN_X = 15;
const width = SANKEY_SVG_WIDTH;
const height = SANKEY_SVG_HEIGHT;
const NODE_PADDING = 40;
const TOOLTIP_CURSOR_OFFSET = 10;
const TOOLTIP_MAX_WIDTH = 200;
const LINK_OPACITY_DEFAULT = 0.4;
const LINK_OPACITY_HIGHLIGHT = 0.6;
const LINK_OPACITY_BACKGROUND = 0.1;
const NODE_CORNER_RADIUS = 0.9;
const NODE_FILL_OPACITY = 1;
const TEXT_FONT_SIZE = 14;
const TEXT_DY = "0.35rem";
const TEXT_NODE_OFFSET = 6;
const NODE_MIDPOINT_DIVISOR = 2;
const MIN_LINK_VALUE = 1;
const MIN_LINK_WIDTH = 1.5;
const MIN_NODE_HEIGHT = 4;
const PROJECT_ID_HANSWEERT = "308a84bb-fc3a-442e-92c9-5508086385f0";
const PROJECT_ID_AMSTERDAM = "d85bd393-6cc3-4461-bcf0-78365d5bbf12";
const PROJECT_NAME_HANSWEERT = "Hansweert";
const PROJECT_NAME_AMSTERDAM = "Amsterdam";
const NODE_ID_WATTHUB = "WATTHUB";
const LABEL_GRID_CONNECTION = "Grid Connection";
const NO_ACTIVITY_LOGGED_LABEL = "No Activity logged";
const FLEET_DISCHARGE_ENERGY_LABEL = "Fleet Discharge Energy";
const sankeyGenerator = sankey()
    .nodeWidth(SANKEY_NODE_WIDTH)
    .nodePadding(NODE_PADDING)
    .extent([
    [MARGIN_X, MARGIN_Y],
    [width - MARGIN_X, height - MARGIN_Y]
])
    .nodeId((node) => node.id)
    .nodeAlign(sankeyCenter);
const SankeyChart = ({ data, projectId, projectName }) => {
    const [hoveredNode, setHoveredNode] = useState(null);
    const [tooltip, setTooltip] = useState(null);
    const svgRef = useRef(null);
    const [dimensions, setDimensions] = useState({ height, width });
    const containerRef = useRef(null);
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const { height, width } = containerRef.current.getBoundingClientRect();
                setDimensions({ height, width });
            }
        };
        updateDimensions();
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);
    if (!data?.nodes?.length || !data?.links?.length) {
        return null;
    }
    const { links, maxY, nodes } = useMemo(() => {
        const clonedData = {
            links: data.links.map((l) => ({ ...l })),
            nodes: data.nodes.map((n) => ({ ...n }))
        };
        const { links, nodes } = sankeyGenerator(clonedData);
        links.forEach((link) => {
            link.width = link.value > 0 ? Math.max(link.width, MIN_LINK_WIDTH) : 0;
        });
        nodes.forEach((node) => {
            const incomingWidth = node.targetLinks.reduce((sum, link) => sum + link.width, 0);
            const outgoingWidth = node.sourceLinks.reduce((sum, link) => sum + link.width, 0);
            const requiredHeight = Math.max(MIN_NODE_HEIGHT, incomingWidth, outgoingWidth);
            const currentHeight = node.y1 - node.y0;
            if (currentHeight < requiredHeight) {
                const center = (node.y0 + node.y1) / 2;
                node.y0 = center - requiredHeight / 2;
                node.y1 = center + requiredHeight / 2;
            }
            const nodeHeight = node.y1 - node.y0;
            if (node.sourceLinks.length > 0) {
                const sorted = [...node.sourceLinks].sort((a, b) => a.y0 - b.y0);
                const offset = (nodeHeight - outgoingWidth) / 2;
                let cursor = node.y0 + offset;
                sorted.forEach((link) => {
                    link.y0 = cursor + link.width / 2;
                    cursor += link.width;
                });
            }
            if (node.targetLinks.length > 0) {
                const sorted = [...node.targetLinks].sort((a, b) => a.y1 - b.y1);
                const offset = (nodeHeight - incomingWidth) / 2;
                let cursor = node.y0 + offset;
                sorted.forEach((link) => {
                    link.y1 = cursor + link.width / 2;
                    cursor += link.width;
                });
            }
        });
        const columns = new Map();
        nodes.forEach((node) => {
            const col = node.xPos ?? node.x0;
            if (!columns.has(col))
                columns.set(col, []);
            columns.get(col).push(node);
        });
        columns.forEach((colNodes) => {
            colNodes.sort((a, b) => a.y0 - b.y0);
            for (let i = 1; i < colNodes.length; i++) {
                const prev = colNodes[i - 1];
                const curr = colNodes[i];
                const minTop = prev.y1 + NODE_PADDING;
                if (curr.y0 < minTop) {
                    const shift = minTop - curr.y0;
                    curr.y0 += shift;
                    curr.y1 += shift;
                    curr.sourceLinks.forEach((link) => {
                        link.y0 += shift;
                    });
                    curr.targetLinks.forEach((link) => {
                        link.y1 += shift;
                    });
                }
            }
        });
        const maxY = Math.max(SANKEY_SVG_HEIGHT, ...nodes.map((n) => n.y1 + MARGIN_Y));
        return { links, maxY, nodes };
    }, [data]);
    const isLinkHighLighted = (link) => {
        return hoveredNode === link.source.index || hoveredNode === link.target.index;
    };
    const getLinkOpacity = (link) => {
        if (hoveredNode === null)
            return LINK_OPACITY_DEFAULT;
        return isLinkHighLighted(link) ? LINK_OPACITY_HIGHLIGHT : LINK_OPACITY_BACKGROUND;
    };
    const handleMouseMove = (event, content) => {
        if (svgRef.current) {
            const svgBounds = svgRef.current.getBoundingClientRect();
            const xScale = dimensions.width / svgBounds.width;
            const yScale = dimensions.height / svgBounds.height;
            const mouseX = (event.clientX - svgBounds.left) * xScale;
            const mouseY = (event.clientY - svgBounds.top) * yScale;
            let x = mouseX + TOOLTIP_CURSOR_OFFSET;
            const y = mouseY + TOOLTIP_CURSOR_OFFSET;
            // If tooltip would extend beyond the right edge, position it to the left of the cursor
            if (x + TOOLTIP_MAX_WIDTH > dimensions.width) {
                x = mouseX - TOOLTIP_CURSOR_OFFSET - TOOLTIP_MAX_WIDTH;
            }
            setTooltip({ content, x, y });
        }
    };
    const getConnectionText = (projectName, projectId, id) => {
        if (id?.toLowerCase() === NO_ACTIVITY_LOGGED_LABEL.toLowerCase()) {
            return FLEET_DISCHARGE_ENERGY_LABEL;
        }
        return (projectName === PROJECT_NAME_HANSWEERT &&
            projectId === PROJECT_ID_HANSWEERT &&
            id === NODE_ID_WATTHUB) ||
            (projectName === PROJECT_NAME_AMSTERDAM && projectId === PROJECT_ID_AMSTERDAM && id === NODE_ID_WATTHUB)
            ? LABEL_GRID_CONNECTION
            : id;
    };
    const handleNodeHover = (event, node) => {
        const isNoActivityNode = node.id?.toLowerCase() === NO_ACTIVITY_LOGGED_LABEL.toLowerCase();
        if (isNoActivityNode) {
            setHoveredNode(node.index);
            handleMouseMove(event, `ID: ${FLEET_DISCHARGE_ENERGY_LABEL}\nDischarge Energy: ${getIncomingSummary(node)}`);
            return;
        }
        const text = getConnectionText(projectName, projectId, node.id);
        setHoveredNode(node.index);
        handleMouseMove(event, `ID: ${text}\nCharge Energy: ${getIncomingSummary(node)}\nDischarge Energy: ${getOutgoingSummary(node)}`);
    };
    const handleLinkHover = (event, link) => {
        const sourceText = getConnectionText(projectName, projectId, link.source.id);
        const targetText = getConnectionText(projectName, projectId, link.target.id);
        handleMouseMove(event, `Source: ${sourceText}\nTarget: ${targetText}\nValue: ${formatTooltipValues(link.value)}`);
    };
    const handleMouseLeave = () => {
        setHoveredNode(null);
        setTooltip(null);
    };
    const allNodes = nodes.map((node) => {
        node.x0 = node.xPos;
        node.x1 = node.xPos + sankeyGenerator.nodeWidth();
        const nodeHeight = node.y1 - node.y0;
        const text = getConnectionText(projectName, projectId, node.id);
        return (_jsxs("g", { children: [_jsx("rect", { fill: getSankeyNodeColor(node.type), fillOpacity: NODE_FILL_OPACITY, height: nodeHeight, onMouseEnter: (e) => handleNodeHover(e, node), onMouseLeave: handleMouseLeave, rx: NODE_CORNER_RADIUS, stroke: "black", width: sankeyGenerator.nodeWidth(), x: node.x0, y: node.y0 }), nodeHeight > 0 && (_jsx("text", { dy: TEXT_DY, fontSize: TEXT_FONT_SIZE, textAnchor: node.x0 < width / NODE_MIDPOINT_DIVISOR ? "start" : "end", x: node.xPos < width / NODE_MIDPOINT_DIVISOR
                        ? node.x1 + TEXT_NODE_OFFSET
                        : node.xPos - TEXT_NODE_OFFSET, y: (node.y1 + node.y0) / NODE_MIDPOINT_DIVISOR, children: getNodeLabel(text) }))] }, node.index));
    });
    const allLinks = links.map((link) => {
        const linkGenerator = sankeyLinkHorizontal();
        const path = linkGenerator(link);
        return (_jsx("g", { children: _jsx("path", { d: path, fill: "none", onMouseEnter: (e) => handleLinkHover(e, link), onMouseLeave: handleMouseLeave, stroke: getSankeyNodeColor(link.source.type), strokeOpacity: getLinkOpacity(link), strokeWidth: link.width }, link.index) }, link.index));
    });
    return (_jsxs("div", { className: "sankey-component", ref: containerRef, children: [_jsxs("svg", { "data-testid": "sankey-svg", height: "100%", preserveAspectRatio: "xMidYMid meet", ref: svgRef, viewBox: `0 0 ${SANKEY_SVG_WIDTH} ${maxY}`, width: "100%", children: [allNodes, allLinks] }), tooltip && (_jsx("div", { className: "sankey-tooltip", style: {
                    left: `${tooltip.x}px`,
                    top: `${tooltip.y}px`
                }, children: tooltip.content }))] }));
};
const getIncomingSummary = (node) => {
    let incoming = 0;
    node.targetLinks.forEach((link) => {
        incoming = incoming + link.value;
    });
    return formatTooltipValues(incoming);
};
const getOutgoingSummary = (node) => {
    let incoming = 0;
    node.sourceLinks.forEach((link) => {
        incoming = incoming + link.value;
    });
    return formatTooltipValues(incoming);
};
const formatTooltipValues = (value) => {
    if (value < MIN_LINK_VALUE)
        return "No Data";
    return `${Math.round(value)} kWh`;
};
const getNodeLabel = (value) => {
    if (value.includes("|")) {
        const values = value.split("|");
        return values[0].concat(" & more");
    }
    return value;
};
export default SankeyChart;
